import { createServerFn } from "@tanstack/react-start";
import { eq, desc, asc, and } from "drizzle-orm";
import { z } from "zod";
import { getDb, schema } from "../db/client.server";
import { cacheGet, cacheSet, cacheDel, cacheDelPattern } from "../cache/redis.server";
import type { ArticleBlock } from "../types/content";
import { parseFirmContact, type FirmContact } from "../firm-contact";
import { parseBooleanSetting } from "../site-settings";
import { parseSiteNavSettings } from "../navigation/defaults";
import type { SiteNavSettings } from "../navigation/types";
import { subscribeEmailToNewsletter } from "../newsletter/subscribe.server";

function mapInsight(row: typeof schema.insights.$inferSelect) {
  return {
    id: row.id,
    slug: row.slug,
    title: row.title,
    category: row.category ?? "",
    tags: Array.isArray(row.tags) ? row.tags.filter((t): t is string => typeof t === "string") : [],
    excerpt: row.excerpt ?? "",
    readTime: row.readTimeMinutes ? `${row.readTimeMinutes} min read` : "5 min read",
    date: row.publishedAt?.toISOString().slice(0, 10) ?? "",
    cover: row.coverUrl ?? "",
    body: (row.contentBlocks ?? []) as ArticleBlock[],
    author: {
      name: row.authorName ?? "",
      slug: row.authorSlug ?? "",
      designation: row.authorDesignation ?? "",
    },
    contentHtml: row.contentHtml ?? "",
    contentFormat: row.contentFormat,
    craftState: row.craftState,
    craftCustomCss: row.craftCustomCss ?? "",
    editorDoc: (row.editorDoc ?? []) as unknown[],
  };
}

export const getPublishedInsights = createServerFn({ method: "GET" }).handler(async () => {
  const cached = await cacheGet<ReturnType<typeof mapInsight>[]>("insights:list");
  if (cached) return cached;

  const db = getDb();
  const rows = await db
    .select()
    .from(schema.insights)
    .where(eq(schema.insights.status, "published"))
    .orderBy(desc(schema.insights.publishedAt));

  const result = rows.map(mapInsight);
  await cacheSet("insights:list", result, 600);
  return result;
});

export const getInsightBySlug = createServerFn({ method: "GET" })
  .inputValidator(z.object({ slug: z.string() }))
  .handler(async ({ data }) => {
    const cacheKey = `insight:slug:${data.slug}`;
    const cached = await cacheGet<ReturnType<typeof mapInsight>>(cacheKey);
    if (cached) return cached;

    const db = getDb();
    const [row] = await db
      .select()
      .from(schema.insights)
      .where(and(eq(schema.insights.slug, data.slug), eq(schema.insights.status, "published")))
      .limit(1);

    if (!row) return null;
    const result = mapInsight(row);
    await cacheSet(cacheKey, result, 3600);
    return result;
  });

export const getPublishedStories = createServerFn({ method: "GET" }).handler(async () => {
  const cached = await cacheGet<unknown[]>("stories:list");
  if (cached) return cached;

  const db = getDb();
  const rows = await db
    .select()
    .from(schema.clientStories)
    .where(eq(schema.clientStories.status, "published"))
    .orderBy(desc(schema.clientStories.publishedAt));

  const result = rows.map((row) => ({
    id: row.id,
    slug: row.slug,
    title: row.title,
    client: row.client ?? "",
    industry: row.industry ?? "",
    service: row.service ?? "",
    excerpt: row.excerpt ?? "",
    date: row.publishedAt?.toISOString().slice(0, 10) ?? "",
    cover: row.coverUrl ?? "",
    readTime: row.readTimeMinutes ? `${row.readTimeMinutes} min read` : "5 min read",
    outcomes: (row.outcomes ?? []) as { label: string; value: string }[],
    body: (row.contentBlocks ?? []) as ArticleBlock[],
    craftState: row.craftState,
    craftCustomCss: row.craftCustomCss ?? "",
    editorDoc: (row.editorDoc ?? []) as unknown[],
  }));

  await cacheSet("stories:list", result, 600);
  return result;
});

export const getStoryBySlug = createServerFn({ method: "GET" })
  .inputValidator(z.object({ slug: z.string() }))
  .handler(async ({ data }) => {
    const cacheKey = `story:slug:${data.slug}`;
    const cached = await cacheGet<unknown>(cacheKey);
    if (cached) return cached;

    const db = getDb();
    const [row] = await db
      .select()
      .from(schema.clientStories)
      .where(and(eq(schema.clientStories.slug, data.slug), eq(schema.clientStories.status, "published")))
      .limit(1);

    if (!row) return null;
    const result = {
      slug: row.slug,
      title: row.title,
      client: row.client ?? "",
      industry: row.industry ?? "",
      service: row.service ?? "",
      excerpt: row.excerpt ?? "",
      date: row.publishedAt?.toISOString().slice(0, 10) ?? "",
      cover: row.coverUrl ?? "",
      readTime: row.readTimeMinutes ? `${row.readTimeMinutes} min read` : "5 min read",
      outcomes: (row.outcomes ?? []) as { label: string; value: string }[],
      body: (row.contentBlocks ?? []) as ArticleBlock[],
      craftState: row.craftState,
      craftCustomCss: row.craftCustomCss ?? "",
      editorDoc: (row.editorDoc ?? []) as unknown[],
    };
    await cacheSet(cacheKey, result, 3600);
    return result;
  });

export const getPublishedCareers = createServerFn({ method: "GET" }).handler(async () => {
  const cached = await cacheGet<unknown[]>("careers:list");
  if (cached) return cached;

  const db = getDb();
  const rows = await db
    .select()
    .from(schema.careers)
    .where(eq(schema.careers.status, "published"))
    .orderBy(asc(schema.careers.sortOrder));

  const result = rows.map((row) => ({
    id: row.id,
    slug: row.slug,
    title: row.title,
    location: row.location ?? "",
    employmentType: row.employmentType ?? "",
    department: row.department ?? "",
    applyEmail: row.applyEmail ?? "",
    excerpt: row.excerpt ?? "",
    contentHtml: row.contentHtml ?? "",
    craftState: row.craftState,
    craftCustomCss: row.craftCustomCss ?? "",
    editorDoc: (row.editorDoc ?? []) as unknown[],
  }));

  await cacheSet("careers:list", result, 600);
  return result;
});

export const getCareerBySlug = createServerFn({ method: "GET" })
  .inputValidator(z.object({ slug: z.string() }))
  .handler(async ({ data }) => {
    const cacheKey = `career:slug:${data.slug}`;
    const cached = await cacheGet<unknown>(cacheKey);
    if (cached) return cached;

    const db = getDb();
    const [row] = await db
      .select()
      .from(schema.careers)
      .where(and(eq(schema.careers.slug, data.slug), eq(schema.careers.status, "published")))
      .limit(1);

    if (!row) return null;
    const result = {
      id: row.id,
      slug: row.slug,
      title: row.title,
      location: row.location ?? "",
      employmentType: row.employmentType ?? "",
      department: row.department ?? "",
      applyEmail: row.applyEmail ?? "",
      excerpt: row.excerpt ?? "",
      contentHtml: row.contentHtml ?? "",
      craftState: row.craftState,
      craftCustomCss: row.craftCustomCss ?? "",
      editorDoc: (row.editorDoc ?? []) as unknown[],
    };
    await cacheSet(cacheKey, result, 3600);
    return result;
  });

export const getServiceCategories = createServerFn({ method: "GET" }).handler(async () => {
  const cached = await cacheGet<unknown[]>("services:list");
  if (cached) return cached;

  const db = getDb();
  const categories = await db
    .select()
    .from(schema.serviceCategories)
    .where(eq(schema.serviceCategories.status, "published"))
    .orderBy(asc(schema.serviceCategories.sortOrder));

  const result = await Promise.all(
    categories.map(async (cat) => {
      const items = await db
        .select()
        .from(schema.serviceItems)
        .where(eq(schema.serviceItems.categoryId, cat.id))
        .orderBy(asc(schema.serviceItems.sortOrder));
      return {
        slug: cat.slug,
        name: cat.name,
        short: cat.short ?? "",
        intro: cat.intro ?? "",
        image: cat.imageUrl ?? "",
        icon: cat.iconName ?? null,
        items: items.map((i) => ({
          name: i.name,
          description: i.description ?? "",
          image: i.imageUrl ?? undefined,
        })),
      };
    }),
  );

  await cacheSet("services:list", result, 3600);
  return result;
});

export type PublicSiteSettings = {
  googleAnalyticsId: string;
  siteUrl: string;
  firmContact: FirmContact;
  nav: SiteNavSettings;
  craftGlobalCss: string;
  cardAnimationsEnabled: boolean;
};

function mapPage(row: typeof schema.pages.$inferSelect) {
  return {
    id: row.id,
    slug: row.slug,
    title: row.title,
    craftState: row.craftState,
    craftCustomCss: row.craftCustomCss ?? "",
    metaTitle: row.metaTitle ?? "",
    metaDescription: row.metaDescription ?? "",
    ogImageMediaId: row.ogImageMediaId,
    isSystemPage: row.isSystemPage,
  };
}

export const getPageBySlug = createServerFn({ method: "GET" })
  .inputValidator(z.object({ slug: z.string() }))
  .handler(async ({ data }) => {
    const cacheKey = `page:slug:${data.slug}`;
    const cached = await cacheGet<ReturnType<typeof mapPage>>(cacheKey);
    if (cached) return cached;

    const db = getDb();
    const [row] = await db
      .select()
      .from(schema.pages)
      .where(and(eq(schema.pages.slug, data.slug), eq(schema.pages.status, "published")))
      .limit(1);

    if (!row) return null;
    const result = mapPage(row);
    await cacheSet(cacheKey, result, 3600);
    return result;
  });

export const getSiteSettingsPublic = createServerFn({ method: "GET" }).handler(async () => {
  const cached = await cacheGet<PublicSiteSettings>("settings:public");
  if (cached) return cached;

  const db = getDb();
  const rows = await db.select().from(schema.siteSettings);
  const map = Object.fromEntries(rows.map((r) => [r.key, r.value]));
  const result: PublicSiteSettings = {
    googleAnalyticsId: String(map.google_analytics_id ?? ""),
    siteUrl: String(map.site_url ?? ""),
    firmContact: parseFirmContact(map.firm_contact),
    nav: parseSiteNavSettings({
      header_menu: map.header_menu,
      footer_menu: map.footer_menu,
      footer_service_slugs: map.footer_service_slugs,
    }),
    craftGlobalCss: String(map.craft_global_css ?? ""),
    cardAnimationsEnabled: parseBooleanSetting(map.card_animations_enabled, true),
  };
  await cacheSet("settings:public", result, 3600);
  return result;
});

export const submitEnquiry = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      name: z.string().min(1),
      email: z.string().email(),
      company: z.string().optional(),
      phone: z.string().optional(),
      designation: z.string().optional(),
      message: z.string().min(1),
      source: z.enum(["contact", "enquiry_dialog", "floating"]).default("contact"),
    }),
  )
  .handler(async ({ data }) => {
    const { getClientIp } = await import("../analytics/ip.server");
    const { rateLimitCheck } = await import("../cache/redis.server");

    const ip = getClientIp();
    const ipAllowed = await rateLimitCheck(`enquiry:ip:${ip}`, 10, 3600);
    if (!ipAllowed) throw new Error("Too many enquiries from this network. Please try again later.");

    const emailAllowed = await rateLimitCheck(
      `enquiry:email:${data.email.toLowerCase()}`,
      10,
      3600,
    );
    if (!emailAllowed) throw new Error("Too many enquiries from this email. Please try again later.");

    const db = getDb();
    const [inserted] = await db
      .insert(schema.contactEnquiries)
      .values({
        name: data.name,
        email: data.email,
        company: data.company,
        phone: data.phone,
        designation: data.designation,
        message: data.message,
        source: data.source,
        status: "new",
      })
      .returning();

    if (!inserted) {
      throw new Error("Failed to save enquiry.");
    }

    try {
      const { sendEnquiryNotification } = await import("../email/enquiry-notification.server");
      await sendEnquiryNotification({
        id: inserted.id,
        name: inserted.name,
        email: inserted.email,
        company: inserted.company,
        phone: inserted.phone,
        designation: inserted.designation,
        message: inserted.message,
        source: inserted.source ?? data.source,
        createdAt: inserted.createdAt,
      });
    } catch (err) {
      console.error("[submitEnquiry] notification email failed:", {
        source: data.source,
        enquiryId: inserted.id,
        error: err,
      });
    }

    return { ok: true };
  });

export const subscribeNewsletter = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      email: z.string().email(),
      source: z.string().optional(),
    }),
  )
  .handler(async ({ data }) => {
    return subscribeEmailToNewsletter(data.email, data.source ?? "insights_newsletter");
  });

export async function invalidateInsightCache(slug?: string) {
  await cacheDel("insights:list");
  await cacheDelPattern("insights:*");
  await cacheDelPattern("insight:*");
  if (slug) await cacheDel(`insight:slug:${slug}`);
}

export async function invalidateInsightTagCache() {
  await cacheDel("insight-tags:list");
  await cacheDel("insight-tags:featured");
}

export const getPublishedInsightTags = createServerFn({ method: "GET" }).handler(async () => {
  const cached = await cacheGet<{ id: string; name: string; slug: string; featured: boolean }[]>("insight-tags:list");
  if (cached) return cached;

  const db = getDb();
  const rows = await db
    .select({
      id: schema.insightTags.id,
      name: schema.insightTags.name,
      slug: schema.insightTags.slug,
      featured: schema.insightTags.featured,
    })
    .from(schema.insightTags)
    .where(eq(schema.insightTags.status, "published"))
    .orderBy(asc(schema.insightTags.sortOrder), asc(schema.insightTags.name));

  await cacheSet("insight-tags:list", rows, 600);
  return rows;
});

export async function invalidateStoryCache(slug?: string) {
  await cacheDelPattern("stories:*");
  if (slug) await cacheDelPattern(`story:slug:${slug}`);
}

export async function invalidateServicesCache() {
  await cacheDelPattern("services:*");
}

export async function invalidateCareersCache() {
  await cacheDel("careers:list");
  await cacheDelPattern("career:*");
}

function mapLeader(row: typeof schema.leaders.$inferSelect) {
  return {
    slug: row.slug,
    name: row.name,
    navLabel: row.navLabel,
    designation: row.designation,
    qualifications: row.qualifications ?? "",
    memberships: (row.memberships ?? []) as string[],
    bio: (row.bio ?? []) as string[],
    initials: row.initials ?? "",
    image: row.portraitUrl ?? "",
  };
}

export const getPublishedLeaders = createServerFn({ method: "GET" }).handler(async () => {
  const cached = await cacheGet<ReturnType<typeof mapLeader>[]>("leaders:list");
  if (cached) return cached;

  const db = getDb();
  const rows = await db
    .select()
    .from(schema.leaders)
    .where(eq(schema.leaders.status, "published"))
    .orderBy(asc(schema.leaders.sortOrder), asc(schema.leaders.name));

  const result = rows.map(mapLeader);
  await cacheSet("leaders:list", result, 600);
  return result;
});

export const getLeaderBySlug = createServerFn({ method: "GET" })
  .inputValidator(z.object({ slug: z.string() }))
  .handler(async ({ data }) => {
    const cacheKey = `leader:slug:${data.slug}`;
    const cached = await cacheGet<ReturnType<typeof mapLeader>>(cacheKey);
    if (cached) return cached;

    const db = getDb();
    const [row] = await db
      .select()
      .from(schema.leaders)
      .where(and(eq(schema.leaders.slug, data.slug), eq(schema.leaders.status, "published")))
      .limit(1);

    if (!row) return null;
    const result = mapLeader(row);
    await cacheSet(cacheKey, result, 3600);
    return result;
  });

export async function invalidateLeadersCache(slugs?: string | string[]) {
  await cacheDelPattern("leaders:*");
  const list = slugs ? (Array.isArray(slugs) ? slugs : [slugs]) : [];
  for (const slug of list) {
    if (slug) await cacheDelPattern(`leader:slug:${slug}`);
  }
}

function mapWhoWeServeItem(row: typeof schema.whoWeServeItems.$inferSelect) {
  return {
    id: row.id,
    name: row.name,
    body: row.body ?? "",
    image: row.imageUrl ?? "",
  };
}

function mapTestimonial(row: typeof schema.testimonials.$inferSelect) {
  return {
    id: row.id,
    quote: row.quote,
    name: row.name,
    role: row.role,
    image: row.imageUrl ?? "",
  };
}

export const getPublishedWhoWeServe = createServerFn({ method: "GET" }).handler(async () => {
  const cached = await cacheGet<ReturnType<typeof mapWhoWeServeItem>[]>("marketing:who-we-serve");
  if (cached) return cached;

  const rows = await getDb()
    .select()
    .from(schema.whoWeServeItems)
    .where(eq(schema.whoWeServeItems.status, "published"))
    .orderBy(asc(schema.whoWeServeItems.sortOrder), asc(schema.whoWeServeItems.name));

  const result = rows.map(mapWhoWeServeItem);
  if (result.length) await cacheSet("marketing:who-we-serve", result, 600);
  return result;
});

export const getPublishedTestimonials = createServerFn({ method: "GET" }).handler(async () => {
  const cached = await cacheGet<ReturnType<typeof mapTestimonial>[]>("marketing:testimonials");
  if (cached) return cached;

  const rows = await getDb()
    .select()
    .from(schema.testimonials)
    .where(eq(schema.testimonials.status, "published"))
    .orderBy(asc(schema.testimonials.sortOrder), asc(schema.testimonials.name));

  const result = rows.map(mapTestimonial);
  if (result.length) await cacheSet("marketing:testimonials", result, 600);
  return result;
});

export async function invalidateMarketingCache() {
  await cacheDelPattern("marketing:*");
}

export async function invalidateSettingsCache() {
  await cacheDelPattern("settings:*");
}

export async function invalidatePageCache(slug?: string) {
  await cacheDelPattern("pages:*");
  if (slug) await cacheDelPattern(`page:slug:${slug}`);
}

/** Fresh published content for site search — bypasses Redis cache. */
export const getPublicSearchIndexData = createServerFn({ method: "GET" }).handler(async () => {
  const db = getDb();
  const [settingsRows, categories, insightRows, storyRows, leaderRows, careerRows] = await Promise.all([
    db.select().from(schema.siteSettings),
    db
      .select()
      .from(schema.serviceCategories)
      .where(eq(schema.serviceCategories.status, "published"))
      .orderBy(asc(schema.serviceCategories.sortOrder)),
    db
      .select({
        slug: schema.insights.slug,
        title: schema.insights.title,
        excerpt: schema.insights.excerpt,
        category: schema.insights.category,
      })
      .from(schema.insights)
      .where(eq(schema.insights.status, "published"))
      .orderBy(desc(schema.insights.publishedAt)),
    db
      .select({
        slug: schema.clientStories.slug,
        title: schema.clientStories.title,
        excerpt: schema.clientStories.excerpt,
        client: schema.clientStories.client,
        industry: schema.clientStories.industry,
        service: schema.clientStories.service,
      })
      .from(schema.clientStories)
      .where(eq(schema.clientStories.status, "published"))
      .orderBy(desc(schema.clientStories.publishedAt)),
    db
      .select()
      .from(schema.leaders)
      .where(eq(schema.leaders.status, "published"))
      .orderBy(asc(schema.leaders.sortOrder), asc(schema.leaders.name)),
    db
      .select({
        slug: schema.careers.slug,
        title: schema.careers.title,
        excerpt: schema.careers.excerpt,
        department: schema.careers.department,
        location: schema.careers.location,
      })
      .from(schema.careers)
      .where(eq(schema.careers.status, "published"))
      .orderBy(asc(schema.careers.sortOrder)),
  ]);

  const settingsMap = Object.fromEntries(settingsRows.map((r) => [r.key, r.value]));
  const firmContact = parseFirmContact(settingsMap.firm_contact);

  const services = await Promise.all(
    categories.map(async (cat) => {
      const items = await db
        .select()
        .from(schema.serviceItems)
        .where(eq(schema.serviceItems.categoryId, cat.id))
        .orderBy(asc(schema.serviceItems.sortOrder));
      return {
        slug: cat.slug,
        name: cat.name,
        short: cat.short ?? "",
        intro: cat.intro ?? "",
        image: cat.imageUrl ?? "",
        icon: cat.iconName ?? null,
        items: items.map((i) => ({
          name: i.name,
          description: i.description ?? "",
          image: i.imageUrl ?? undefined,
        })),
      };
    }),
  );

  return {
    services,
    insights: insightRows.map((row) => ({
      slug: row.slug,
      title: row.title,
      excerpt: row.excerpt ?? "",
      category: row.category ?? "",
    })),
    stories: storyRows.map((row) => ({
      slug: row.slug,
      title: row.title,
      excerpt: row.excerpt ?? "",
      client: row.client ?? undefined,
      industry: row.industry ?? undefined,
      service: row.service ?? undefined,
    })),
    leaders: leaderRows.map(mapLeader),
    careers: careerRows.map((row) => ({
      slug: row.slug,
      title: row.title,
      excerpt: row.excerpt ?? undefined,
      department: row.department ?? undefined,
      location: row.location ?? undefined,
    })),
    firmContact,
  };
});
